import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {classNames} from 'primereact/utils';
import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';
import {Dialog} from 'primereact/dialog';
import {useAppDispatch, useAppSelector} from '../../../redux/hooks';
import {Paginator} from 'primereact/paginator';
import {ColumnGroup} from 'primereact/columngroup';
import {Row} from 'primereact/row';
import TrunkStatisticsListNav from './TrunkStatisticsListNav';
import TrunkStatisticsDetails from './TrunkStatisticsDetails';
import {
    getTrunkStatisticDetailsInfo,
    initialTrunkStatisticsData,
    resetTrunkStatisticFilters,
    setTrunkStatisticsCurrentPage,
    setTrunkStatisticsCurrentPageSize,
    setTrunkStatisticsFilter,
    setTrunkStatisticsUiFilters,
} from '../../../redux/actions/actionsTrunkStatistics';
import {getTrunkStatisticsWithPaginationFilterAndSorting} from '../../../redux/api/apiTrunkStatisticsList';
import {GET_TRUNK_STATISTICS} from '../../../redux/types/typesTrunkStatisticsList';
import {
    areSearchParamsEqual,
    buildTrunkStatisticsSearchParams,
    readInitialTrunkStatisticsUrlState
} from '../../../utils/trunkStatisticsUrlState';

const TrunkStatisticsList = (props: any) => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [initialTrunkStatisticsUrlState] = useState(readInitialTrunkStatisticsUrlState);
    const trunkStatisticsList = useAppSelector(state => state.TrunkStatisticsListReducer?.trunkStatisticsList);
    const paginationSize = useAppSelector(state => state.TrunkStatisticsListReducer.paginationSize);
    const pagination = useAppSelector(state => state.TrunkStatisticsListReducer.pagination);
    const loader = useAppSelector(state => state.TrunkStatisticsListReducer.loading);
    const paginationPage = useAppSelector(state => state.TrunkStatisticsListReducer.paginationPage);
    const clearFilters = useAppSelector(state => state.TrunkStatisticsListReducer.clearFilters);
    const statisticTrunkData = useAppSelector(state => state.TrunkStatisticsListReducer.statisticTrunkData);
    const activeClick = useAppSelector(state => state.UsersListReducer.activeClick);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);

    const [first, setFirst] = useState(() => (initialTrunkStatisticsUrlState.page - 1) * initialTrunkStatisticsUrlState.size);
    const [orderBy, setOrderBy] = useState<any>(initialTrunkStatisticsUrlState.orderBy || 'TrunkPhoneId');
    const [isDescFilter, setIsDescFilter] = useState<boolean>(initialTrunkStatisticsUrlState.isDesc);
    const [sortOrder, setSortOrder] = useState<number>(initialTrunkStatisticsUrlState.sortOrder);
    const [startTime, setStartTime] = useState<any>(initialTrunkStatisticsUrlState.startTime || new Date('2024-10-29 00:00:00'));
    const [endTime, setEndTime] = useState<any>(() => {
        if (initialTrunkStatisticsUrlState.endTime) return initialTrunkStatisticsUrlState.endTime;
        const d = new Date();
        d.setHours(23, 59, 59, 0);
        return d;
    });
    const [filterDateTime, setFilterDateTime] = useState<any>(initialTrunkStatisticsUrlState.filterDateTime || new Date());
    const [filterEndDateTime, setFilterEndDateTime] = useState<any>(initialTrunkStatisticsUrlState.filterEndDateTime || new Date());
    const [filterId, setFilterId] = useState<any>(initialTrunkStatisticsUrlState.filterId);
    const [filterNumber, setFilterNumber] = useState<any>(initialTrunkStatisticsUrlState.filterNumber);
    const [numberFilterDraft, setNumberFilterDraft] = useState<string>(initialTrunkStatisticsUrlState.filterNumber?.value ?? '');
    const [selectedTrunk, setSelectedTrunk] = useState<any>(initialTrunkStatisticsUrlState.trunkId);
    const [appliedSearch, setAppliedSearch] = useState<{
        trunkId: any;
        filterDateTime: any;
        filterEndDateTime: any;
        startTime: any;
        endTime: any;
    } | null>(() => {
        if (!initialTrunkStatisticsUrlState.hasAppliedSearch || initialTrunkStatisticsUrlState.trunkId == null) {
            return null;
        }
        return {
            trunkId: initialTrunkStatisticsUrlState.trunkId,
            filterDateTime: initialTrunkStatisticsUrlState.filterDateTime || new Date(),
            filterEndDateTime: initialTrunkStatisticsUrlState.filterEndDateTime || new Date(),
            startTime: initialTrunkStatisticsUrlState.startTime || new Date('2024-10-29 00:00:00'),
            endTime: initialTrunkStatisticsUrlState.endTime || (() => {
                const d = new Date();
                d.setHours(23, 59, 59, 0);
                return d;
            })(),
        };
    });
    const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
    const [selectedTrunkName, setSelectedTrunkName] = useState<string>('');
    const [visible, setVisible] = useState(false);
    const [totalItems, setTotalItems] = useState<number>(paginationSize);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const dataTableRef = useRef<DataTable | any>(null);
    const lastRequestKeyRef = useRef<string | null>(null);
    const skipUrlWriteRef = useRef(true);
    const numberFilterCallbackRef = useRef<((value: any, index?: number) => void) | null>(null);
    const hasInitializedPagination = useRef(false);
    const [tableMountKey, setTableMountKey] = useState(0);

    const tableRows = trunkStatisticsList?.trunks ?? trunkStatisticsList?.numbers ?? trunkStatisticsList?.items ?? [];
    const summary = trunkStatisticsList?.all ?? {};

    const getRowId = (row: any) => row?.trunkPhoneId ?? row?.id;
    const getRowNumber = (row: any) => row?.aNumber ?? row?.number ?? row?.phoneNo ?? row?.phone ?? '';

    const formatDate = (dateString: Date) => {
        const dateObj = new Date(dateString);
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        const day = dateObj.getDate().toString().padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const getTime = (dateString: Date | any) => {
        const dateObj = new Date(dateString);
        const hours = dateObj.getHours().toString().padStart(2, '0');
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        const seconds = dateObj.getSeconds().toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const [sortData, setSortData] = useState<any>({
        pageNumber: 1,
        pageSize: 10,
        sort: {
            orderBy: 'TrunkPhoneId',
            isDesc: true,
        },
    });

    useEffect(() => {
        if (initialTrunkStatisticsUrlState.hasAny) {
            return;
        }
        setStartTime(new Date('2024-10-29 00:00:00'));
        setEndTime(() => {
            const d = new Date();
            d.setHours(23, 59, 59, 0);
            return d;
        });
        setFilterDateTime(new Date());
        setFilterEndDateTime(new Date());
        setFirst(0);
        setTotalItems(0);
        dispatch({type: GET_TRUNK_STATISTICS, payload: null});
        dispatch(initialTrunkStatisticsData());
        dispatch(resetTrunkStatisticFilters());
        dispatch(setTrunkStatisticsCurrentPage(1));
        dispatch(setTrunkStatisticsCurrentPageSize(10));
        lastRequestKeyRef.current = null;
    }, [dispatch]);

    useLayoutEffect(() => {
        if (initialTrunkStatisticsUrlState.hasAny) {
            if (Object.keys(initialTrunkStatisticsUrlState.uiFilters || {}).length > 0) {
                dispatch(setTrunkStatisticsUiFilters(initialTrunkStatisticsUrlState.uiFilters));
            } else {
                dispatch(resetTrunkStatisticFilters());
            }
            if (initialTrunkStatisticsUrlState.page !== 1) {
                dispatch(setTrunkStatisticsCurrentPage(initialTrunkStatisticsUrlState.page));
            }
            if (initialTrunkStatisticsUrlState.size !== 10) {
                dispatch(setTrunkStatisticsCurrentPageSize(initialTrunkStatisticsUrlState.size));
            }
            setTableMountKey(1);
            return;
        }

        dispatch(resetTrunkStatisticFilters());
        setTableMountKey(1);
    }, []);

    useEffect(() => {
        if (!trunkStatisticsList) {
            setTotalItems(0);
            return;
        }
        if (pagination.hasNext && pagination.pageNumber) {
            setTotalItems(pagination.pageNumber * paginationSize + paginationSize);
        } else if (!pagination.hasNext) {
            setTotalItems(pagination.pageNumber * paginationSize);
        }
    }, [pagination, paginationSize, trunkStatisticsList]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const resetTableHorizontalScroll = () => {
        const scrollableBody = document.querySelector('.p-datatable-wrapper');
        if (scrollableBody) {
            scrollableBody.scrollTop = 0;
            scrollableBody.scrollLeft = 0;
        }
    };

    const isMobile = () => windowWidth <= 1248;

    const handleSearch = () => {
        if (!selectedTrunk || !filterDateTime || !filterEndDateTime || !startTime || !endTime) return;
        lastRequestKeyRef.current = null;
        dispatch(setTrunkStatisticsCurrentPage(1));
        setFirst(0);
        setAppliedSearch({
            trunkId: selectedTrunk,
            filterDateTime,
            filterEndDateTime,
            startTime,
            endTime,
        });
    };

    const handleRowClick = (rowData: any) => {
        dispatch(getTrunkStatisticDetailsInfo(rowData));
        setSelectedRowData(rowData);
        if (isMobile()) {
            setVisible(true);
        }
    };

    const handleReset = () => {
        setSelectedRowData(null);
        dispatch(initialTrunkStatisticsData());
        dataTableRef?.current?.reset();
        customFilter();
        setStartTime(new Date('2024-10-29 00:00:00'));
        setEndTime(() => {
            const d = new Date();
            d.setHours(23, 59, 59, 0);
            return d;
        });
        setFilterDateTime(new Date());
        setFilterEndDateTime(new Date());
        setSortOrder(0);
        setFirst(0);
        dispatch(resetTrunkStatisticFilters());
        dispatch(setTrunkStatisticsCurrentPage(1));
        dispatch(setTrunkStatisticsCurrentPageSize(10));
        setFilterId(null);
        setFilterNumber(null);
        setSelectedTrunk(null);
        setOrderBy('TrunkPhoneId');
        setIsDescFilter(true);
        setAppliedSearch(null);
        setTotalItems(0);
        setSortData({
            pageNumber: 1,
            pageSize: 10,
            sort: {
                orderBy: 'TrunkPhoneId',
                isDesc: true,
            },
        });
        dispatch({type: GET_TRUNK_STATISTICS, payload: null});
        skipUrlWriteRef.current = true;
        setSearchParams(new URLSearchParams(), {replace: true});
        setTableMountKey((key) => key + 1);
    };

    useEffect(() => {
        if (initialTrunkStatisticsUrlState.hasAny) {
            return;
        }
        handleReset();
    }, []);

    const onPageChange = (event: {first: React.SetStateAction<number>; page: number; rows: any}) => {
        setFirst(event.first);
        dispatch(setTrunkStatisticsCurrentPage(event.page + 1));
        dispatch(setTrunkStatisticsCurrentPageSize(event.rows));
        setSortData((prev: any) => ({...prev, pageNumber: event.page + 1, pageSize: event.rows}));
    };

    useEffect(() => {
        if (!hasInitializedPagination.current) {
            hasInitializedPagination.current = true;
            const urlPage = initialTrunkStatisticsUrlState.page || 1;
            const urlSize = initialTrunkStatisticsUrlState.size || 10;
            const pageChanged = paginationPage !== urlPage;
            const sizeChanged = paginationSize !== urlSize;
            if (pageChanged) {
                dispatch(setTrunkStatisticsCurrentPage(urlPage));
            }
            if (sizeChanged) {
                dispatch(setTrunkStatisticsCurrentPageSize(urlSize));
            }
            if (pageChanged || sizeChanged) {
                return;
            }
        }
        if (!jwtToken?.jwtToken) {
            return;
        }

        let filter: {key: string; value: string}[] = [];
        const dataSort: any = {
            pageNumber: paginationPage,
            pageSize: paginationSize,
            sort: {
                orderBy: orderBy || 'TrunkPhoneId',
                isDesc: isDescFilter,
            },
        };

        if (!appliedSearch) {
            lastRequestKeyRef.current = null;
            return;
        }

        filter.push({key: 'SofiaTrunkId', value: String(appliedSearch.trunkId)});

        if (filterId?.value) {
            filter.push(filterId);
        }
        if (filterNumber?.value) {
            filter.push(filterNumber);
        }

        filter.push({
            key: 'Start',
            value: `${formatDate(appliedSearch.filterDateTime)} ${getTime(appliedSearch.startTime)},${formatDate(appliedSearch.filterEndDateTime)} ${getTime(appliedSearch.endTime)}`,
        });

        dataSort.filter = filter;

        const requestKey = JSON.stringify(dataSort);
        if (lastRequestKeyRef.current === requestKey) {
            return;
        }
        lastRequestKeyRef.current = requestKey;
        dispatch(getTrunkStatisticsWithPaginationFilterAndSorting(dataSort, jwtToken?.jwtToken));
        dispatch(setTrunkStatisticsFilter(dataSort));
        setSortData(dataSort);
        resetTableHorizontalScroll();
    }, [
        appliedSearch,
        paginationPage,
        paginationSize,
        orderBy,
        isDescFilter,
        sortOrder,
        filterId,
        filterNumber,
        jwtToken?.jwtToken,
        dispatch,
    ]);

    useEffect(() => {
        if (skipUrlWriteRef.current) {
            skipUrlWriteRef.current = false;
            return;
        }

        const dateSource = appliedSearch || {
            filterDateTime,
            filterEndDateTime,
            startTime,
            endTime,
        };

        const nextParams = buildTrunkStatisticsSearchParams({
            page: paginationPage,
            size: paginationSize,
            orderBy,
            isDesc: isDescFilter,
            sortOrder,
            trunkId: selectedTrunk,
            filterId,
            filterNumber,
            filterDateTime: dateSource.filterDateTime,
            filterEndDateTime: dateSource.filterEndDateTime,
            startTime: dateSource.startTime,
            endTime: dateSource.endTime,
        });

        if (!areSearchParamsEqual(searchParams, nextParams)) {
            setSearchParams(nextParams, {replace: true});
        }
    }, [
        paginationPage, paginationSize, orderBy, isDescFilter, sortOrder,
        selectedTrunk, filterId, filterNumber, appliedSearch,
        filterDateTime, filterEndDateTime, startTime, endTime,
        searchParams, setSearchParams
    ]);

    useEffect(() => {
        if (activeClick?.item !== '/trunk_statistics') {
            return;
        }

        const fromPath = (activeClick?.from ?? '').replace(/^#/, '');
        const isFromSamePage = fromPath === '/trunk_statistics' || fromPath === 'trunk_statistics';

        if (activeClick?.click && !isFromSamePage) {
            handleReset();
            return;
        }

        const isRelevantClick = isFromSamePage && activeClick?.click;
        if (!isRelevantClick || !appliedSearch || !sortData?.filter?.length) {
            return;
        }

        lastRequestKeyRef.current = JSON.stringify(sortData);
        dispatch(getTrunkStatisticsWithPaginationFilterAndSorting(sortData, jwtToken?.jwtToken));
        resetTableHorizontalScroll();
    }, [activeClick]);

    const monitoringSort = (event: {sortField: any}) => {
        const sortFieldMap: Record<string, string> = {
            Id: 'TrunkPhoneId',
            TrunkPhoneId: 'TrunkPhoneId',
            Number: 'ANumber',
            ANumber: 'ANumber',
            TotalCallsCount: 'AllCalls',
            AllCalls: 'AllCalls',
            TotalCallsDuration: 'AllDuration',
            AllDuration: 'AllDuration',
            IncomingCallsCount: 'InternalCount',
            InternalCount: 'InternalCount',
            IncomingCallsDuration: 'InternalFullduration',
            InternalFullduration: 'InternalFullduration',
            OutgoingCallsCount: 'ExternalCount',
            ExternalCount: 'ExternalCount',
            OutgoingCallsDuration: 'ExternalFullduration',
            ExternalFullduration: 'ExternalFullduration',
        };
        const newOrderBy = sortFieldMap[event.sortField];
        if (!newOrderBy) return;
        setOrderBy(newOrderBy);
        setIsDescFilter(!isDescFilter);
        setSortOrder(sortOrder === 1 ? -1 : 1);
        dispatch(setTrunkStatisticsCurrentPage(1));
        setFirst(0);
    };

    const customFilter = (event?: any) => {
        if (event) {
            const filters = event.filters;
            const setFilter = (filterName: string, key: string, defaultValue?: any) => {
                if (!filters[filterName]) return defaultValue;
                const value = filters[filterName].value || filters[filterName].constraints?.[0]?.value;
                if (!value) return defaultValue;
                if (Array.isArray(value)) {
                    return value.map((item) => ({key, value: String(item)}));
                }
                return {key, value: String(value)};
            };
            setFilterId(setFilter('id', 'TrunkPhoneId', null));
            setFilterNumber(setFilter('number', 'ANumber', null));
        } else {
            setFilterId(null);
            setFilterNumber(null);
            setNumberFilterDraft('');
            setOrderBy('TrunkPhoneId');
            setIsDescFilter(true);
        }
    };

    const filterClearTemplate = (options: {
        filterClearCallback: React.MouseEventHandler<HTMLButtonElement> | undefined;
    }) => (
        <Button
            type="button"
            className="p-button-secondary"
            icon="pi pi-times"
            onClick={(e) => {
                setNumberFilterDraft('');
                options.filterClearCallback?.(e);
            }}
        />
    );

    const filterApplyTemplate = (options: {
        filterApplyCallback: (() => void) | undefined;
    }) => (
        <Button
            type="button"
            icon="pi pi-check"
            onClick={() => {
                numberFilterCallbackRef.current?.(numberFilterDraft || null);
                options.filterApplyCallback?.();
                dispatch(setTrunkStatisticsCurrentPage(1));
            }}
        />
    );

    const numberFilterTemplate = (options: any) => {
        numberFilterCallbackRef.current = options.filterCallback;
        return (
            <>
                <div className="mb-3 text-bold">Введіть номер:</div>
                <InputText
                    value={numberFilterDraft}
                    onChange={(e) => setNumberFilterDraft(e.target.value)}
                    className="p-column-filter"
                    placeholder="Номер"
                />
            </>
        );
    };

    const renderHeader = (field: string, sortField: string) => {
        const sorted = sortField === orderBy;
        const sortIcon = classNames('p-sortable-column-icon', {
            'pi pi-sort-amount-up-alt': sorted && sortOrder === 1,
            'pi pi-fw pi-sort-amount-down': sorted && sortOrder === -1,
            'pi pi-fw pi-sort-alt': sortOrder === 0 || !sorted,
        });
        return (
            <div className="flex align-items-center cursor-pointer mr-2 w-full" onClick={() => monitoringSort({sortField})}>
                <p className="m-0 pr-2">{field}</p><i className={`${sortIcon} pl-2 ${field === 'Номер' ? 'pl-4' : ''}`} />
            </div>
        );
    };

    const convertSecondsToTime = (totalSeconds: number | null | undefined): string => {
        if (totalSeconds === null || totalSeconds === undefined) return '---';
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const remainingSeconds = totalSeconds % 60;
        const seconds = remainingSeconds >= 0.5 ? Math.ceil(remainingSeconds) : Math.floor(remainingSeconds);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const pickCount = (obj: any, keys: string[]) => {
        for (const key of keys) {
            if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
        }
        return 0;
    };

    const pickDuration = (obj: any, keys: string[]) => {
        for (const key of keys) {
            if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
        }
        return null;
    };

    const summaryTotalCount = pickCount(summary, ['allCalls', 'totalCallsCount', 'callsCount', 'count', 'totalCount']);
    const summaryTotalDuration = pickDuration(summary, ['allDuration', 'totalCallsDuration', 'duration', 'totalDuration']);
    const summaryIncomingCount = pickCount(summary, ['internalCount', 'incomingCallsCount', 'incomingCount']);
    const summaryIncomingDuration = pickDuration(summary, ['internalFullduration', 'incomingCallsDuration', 'incomingDuration']);
    const summaryOutgoingCount = pickCount(summary, ['externalCount', 'outgoingCallsCount', 'outgoingCount']);
    const summaryOutgoingDuration = pickDuration(summary, ['externalFullduration', 'outgoingCallsDuration', 'outgoingDuration']);

    const headerNumberBlock = () => (
        <div className="flex">
            <div style={{width: '80px', minWidth: '80px', maxWidth: '80px'}}>{renderHeader('ID', 'TrunkPhoneId')}</div>
            <div style={{width: '180px', minWidth: '180px', maxWidth: '180px'}}>{renderHeader('Номер', 'ANumber')}</div>
        </div>
    );

    const headerCallsBlock = (countField: string, durationField: string) => (
        <div className="flex">
            <div style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>
                {renderHeader('Кількість', countField)}
            </div>
            <div style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>
                {renderHeader('Тривалість', durationField)}
            </div>
        </div>
    );

    const headerSummaryValues = (count: number, duration: number | null, showDashForZero?: boolean) => (
        <div className="flex">
            <div style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>{count ?? 0}</div>
            <div style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>
                {showDashForZero && !count ? '---' : convertSecondsToTime(duration)}
            </div>
        </div>
    );

    const headerGroup = () => (
        <ColumnGroup>
            <Row>
                <Column
                    className="column-border-none"
                    header="Номер транка"
                    headerStyle={{width: '260px', minWidth: '260px', maxWidth: '260px'}}
                />
                <Column
                    className="column-border-none"
                    header="Всього дзвінків"
                    headerStyle={{width: '240px', minWidth: '240px', maxWidth: '240px'}}
                />
                <Column
                    className="column-border-none"
                    header="Вхідні дзвінки"
                    headerStyle={{width: '240px', minWidth: '240px', maxWidth: '240px'}}
                />
                <Column
                    className="column-border-none"
                    header="Вихідні дзвінки"
                    headerStyle={{width: '240px', minWidth: '240px', maxWidth: '240px'}}
                />
            </Row>
            <Row>
                <Column
                    className="Id-FullName-Header trunk-header-number"
                    header={headerNumberBlock}
                    filter
                    filterField="number"
                    showFilterMatchModes={false}
                    filterElement={numberFilterTemplate}
                    filterApply={filterApplyTemplate}
                    filterClear={filterClearTemplate}
                    showFilterMenuOptions={false}
                />
                <Column className="column-border-none" header={headerCallsBlock('AllCalls', 'AllDuration')} />
                <Column className="column-border-none" header={headerCallsBlock('InternalCount', 'InternalFullduration')} />
                <Column className="column-border-none" header={headerCallsBlock('ExternalCount', 'ExternalFullduration')} />
            </Row>
            <Row>
                <Column
                    className="column-border-none"
                    header=""
                    style={{borderRight: '1px solid grey', display: 'table-cell'}}
                />
                <Column
                    className="column-border-none"
                    header={headerSummaryValues(summaryTotalCount, summaryTotalDuration)}
                    style={{borderRight: '1px solid grey'}}
                />
                <Column
                    className="column-border-none"
                    header={headerSummaryValues(summaryIncomingCount, summaryIncomingDuration, true)}
                />
                <Column
                    className="column-border-none"
                    header={headerSummaryValues(summaryOutgoingCount, summaryOutgoingDuration, true)}
                />
            </Row>
        </ColumnGroup>
    );

    const bodyNumberTemplate = (data: any) => (
        <div className="flex align-items-center">
            <div className="flex align-items-center" style={{width: '80px', minWidth: '80px', maxWidth: '80px', minHeight: '33px'}}>{getRowId(data)}</div>
            <div style={{width: '180px', minWidth: '180px', maxWidth: '180px'}}>{getRowNumber(data)}</div>
        </div>
    );

    const bodyCallsTemplate = (data: any, countKeys: string[], durationKeys: string[]) => (
        <div className="flex align-items-center">
            <div style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>{pickCount(data, countKeys)}</div>
            <div style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>
                {convertSecondsToTime(pickDuration(data, durationKeys))}
            </div>
        </div>
    );

    const dynamicRowClass = (data: any) =>
        getRowId(data) === getRowId(selectedRowData) ? 'mainRowMonitoringTable p-highlight' : 'mainRowMonitoringTable';

    return (
        <div className="layout-dashboard">
            <div className="flex flex-wrap col-12 md:col-12 p-0">
                <div
                    className={classNames(
                        'card widget-table col-12 transition-duration-300 monitoring-table align-self-start monitoringLoader m-0 mr-0',
                        {'md:col-12': !selectedRowData || isMobile()},
                    )}
                >
                    <TrunkStatisticsListNav
                        onSearch={handleSearch}
                        setEndTime={setEndTime}
                        endTime={endTime}
                        startTime={startTime}
                        setStartTime={setStartTime}
                        setFilterEndDateTime={setFilterEndDateTime}
                        filterEndDateTime={filterEndDateTime}
                        setFilterDateTime={setFilterDateTime}
                        filterDateTime={filterDateTime}
                        sortData={sortData}
                        resetSorting={handleReset}
                        profileMenuPin={!!props.profileMenuPin}
                        selectedTrunk={selectedTrunk}
                        setSelectedTrunk={setSelectedTrunk}
                        onTrunkChange={(trunk: any) => setSelectedTrunkName(trunk?.displayName ?? trunk?.name ?? '')}
                    />
                    <DataTable
                        key={tableMountKey}
                        headerColumnGroup={headerGroup()}
                        scrollable={windowWidth <= 960}
                        scrollHeight={selectedRowData && !isMobile() ? '33vh' : '70vh'}
                        ref={dataTableRef}
                        rows={10}
                        filters={clearFilters}
                        loading={loader}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        value={tableRows}
                        className="p-datatable-customers monitoring-table monitoring-table-rowGroup statusStatisticks"
                        emptyMessage="Нічого не знайдено"
                        rowHover={false}
                        selectionMode="single"
                        lazy
                        onFilter={customFilter}
                        onRowClick={(e) => handleRowClick(e.data)}
                        rowClassName={dynamicRowClass}
                        dataKey="trunkPhoneId"
                    >
                        <Column body={bodyNumberTemplate} style={{width: '260px', minWidth: '260px', maxWidth: '260px'}} />
                        <Column
                            body={(rowData) =>
                                bodyCallsTemplate(rowData, ['allCalls', 'totalCallsCount', 'callsCount', 'totalCount'], [
                                    'allDuration',
                                    'totalCallsDuration',
                                    'duration',
                                    'totalDuration',
                                ])
                            }
                            style={{width: '240px', minWidth: '240px', maxWidth: '240px', minHeight: '45px'}}
                        />
                        <Column
                            body={(rowData) =>
                                bodyCallsTemplate(rowData, ['internalCount', 'incomingCallsCount', 'incomingCount'], [
                                    'internalFullduration',
                                    'incomingCallsDuration',
                                    'incomingDuration',
                                ])
                            }
                            style={{width: '240px', minWidth: '240px', maxWidth: '240px', minHeight: '45px'}}
                        />
                        <Column
                            body={(rowData) =>
                                bodyCallsTemplate(rowData, ['externalCount', 'outgoingCallsCount', 'outgoingCount'], [
                                    'externalFullduration',
                                    'outgoingCallsDuration',
                                    'outgoingDuration',
                                ])
                            }
                            style={{width: '240px', minWidth: '240px', maxWidth: '240px', minHeight: '45px'}}
                        />
                    </DataTable>
                    <Paginator
                        first={first}
                        rows={paginationSize}
                        totalRecords={totalItems}
                        rowsPerPageOptions={[5, 10, 15, 25, 50]}
                        onPageChange={onPageChange}
                        className="border-top-1 border-bottom-1"
                        template="PrevPageLink PageLinks NextPageLink RowsPerPageDropdown"
                    />
                </div>
                {selectedRowData && statisticTrunkData && (
                    <>
                        {windowWidth > 1248 && (
                            <div className={classNames('card widget-table col-12 md:col-12 mt-4 align-self-start sticky top-0')}>
                                <TrunkStatisticsDetails
                                    setSelectedRowData={setSelectedRowData}
                                    trunkName={selectedTrunkName}
                                />
                            </div>
                        )}
                        {windowWidth <= 1248 && (
                            <Dialog
                                visible={visible}
                                className="monitoring-info"
                                header={
                                    statisticTrunkData?.aNumber ??
                                    statisticTrunkData?.number ??
                                    statisticTrunkData?.phoneNo ??
                                    statisticTrunkData?.phone ??
                                    'Деталі номера'
                                }
                                headerClassName="pl-5 text-xs"
                                onHide={() => {
                                    setVisible(false);
                                    setSelectedRowData(null);
                                    dispatch(initialTrunkStatisticsData());
                                }}
                            >
                                <TrunkStatisticsDetails
                                    setSelectedRowData={(value: any) => {
                                        setSelectedRowData(value);
                                        if (!value) {
                                            setVisible(false);
                                            dispatch(initialTrunkStatisticsData());
                                        }
                                    }}
                                    trunkName={selectedTrunkName}
                                />
                            </Dialog>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TrunkStatisticsList;
